import {
    useEffect,
    useMemo,
    useState,
    useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Row,
    Col,
    Radio,
    Input,
    Form,
    Select,
    Button,
    Space,
    Divider,
    Typography,
} from 'antd';

import MUIFormItem from '@/components/MUIFormItem';
import { actions } from '@/redux/tour/trip/create/reducer';
import { selectors } from '@/redux/tour/trip/create/model';
import { useCurrency } from '@/hooks/useCurrency';
import { firstToUpper } from '@/helpers/string';

const TourDetails = ({ validations }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { icon: currencyIcon } = useCurrency();

    const options = useMemo(() => ([
        { label: firstToUpper(t('days')), value: 2 },
        { label: firstToUpper(t('hours')), value: 1 },
    ]), [t]);

    const costTypeOptions = useMemo(() => ([
        { label: t('%'), value: 1 },
        { label: t(currencyIcon), value: 2 },
    ]), [t]);

    const tripTypes = useSelector(selectors.tripTypes);
    const loading = useSelector(selectors.loading);

    useEffect(() => {
        dispatch(actions.requestGetTripTypes());
    }, [dispatch]);

    // search trip type
    const onSearch = useCallback((searchValue) => {
        if (searchValue.length === 3) {
            dispatch(actions.requestGetTripTypes({
                name: searchValue,
            }));
        }
        if (searchValue.length === 0) {
            dispatch(actions.requestGetTripTypes());
        }
    }, [dispatch]);

    // add new type
    const [typeName, setTypeName] = useState('');

    const onTypeNameChange = (event) => {
        setTypeName(event.target.value);
    };

    const addItem = useCallback(() => {
        dispatch(actions.requestAddNewTripType({
            name: typeName,
            category: 1,
        }));
        setTypeName('');
    }, [dispatch, typeName]);

    return (
        <>
            <Row gutter={30}>
                <Col lg={24} xs={24}>
                    <MUIFormItem
                        label={t('Tour Name')}
                        name="name"
                        rules={validations.name}
                    >
                        <Input />
                    </MUIFormItem>
                </Col>

                <Col xs={24} xxl={12}>
                    <MUIFormItem
                        label={t('Duration')}
                        name="duration"
                        rules={validations.duration}
                    >
                        <Input
                            suffix={(
                                <Form.Item
                                    name="duration_type"
                                    noStyle
                                >
                                    <Radio.Group
                                        options={options}
                                        optionType="button"
                                        buttonStyle="solid"
                                    />
                                </Form.Item>
                            )}
                        />
                    </MUIFormItem>
                </Col>
                <Col xxl={12} xs={24}>
                    <MUIFormItem
                        label={t('Tour Style')}
                        name="tour_types"
                        rules={validations.style}
                    >
                        <Select
                            mode="multiple"
                            showSearch
                            filterOption={false}
                            defaultActiveFirstOption={false}
                            onSearch={onSearch}
                            loading={loading}
                            popupClassName="tour-style-select"
                            // getPopupContainer={(trigger) => {
                            //     console.log(trigger?.parentElement);
                            //     return trigger?.parentElement;
                            // }}
                            dropdownRender={(menu) => (
                                <>
                                    {menu}
                                    <Divider />
                                    <Space
                                        align="center"
                                    >
                                        <Input
                                            placeholder="Type"
                                            value={typeName}
                                            onChange={onTypeNameChange}
                                        />
                                        <Button type="primary" onClick={addItem}>
                                            <Typography.Text>
                                                {t('Add')}
                                            </Typography.Text>
                                        </Button>
                                    </Space>
                                </>
                            )}
                        >
                            {tripTypes.map((type) => (
                                <Select.Option key={type.id} value={type.id}>
                                    {type.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </MUIFormItem>
                </Col>
                <Col lg={24} xs={24}>
                    <MUIFormItem
                        label={t('Cost')}
                        name="cost"
                        rules={validations.cost}
                    >
                        <Input
                            suffix={(
                                <>
                                    {`${currencyIcon}0.00`}
                                    <Form.Item
                                        name="cost_type"
                                        noStyle
                                    >
                                        <Radio.Group
                                            options={costTypeOptions}
                                            optionType="button"
                                            buttonStyle="solid"
                                        />
                                    </Form.Item>
                                </>
                            )}
                        />
                    </MUIFormItem>
                </Col>
            </Row>
        </>
    );
};

export default TourDetails;
